import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import API from "../../services/api";
import { fmtDate, normalizeRows, orDash } from "./AccountsShared";
import "./AccountsExpenses.css";

const CATEGORY_OPTIONS = [
  "Travel",
  "Fuel",
  "Office Supplies",
  "Maintenance",
  "Utilities",
  "Food & Meals",
  "Salary Advance",
  "Marketing",
  "Freight",
  "Packaging",
  "Repair",
  "Other",
];

const DEPARTMENT_OPTIONS = [
  "Accounts",
  "Admin",
  "Production",
  "Purchase",
  "Sales",
  "Logistics",
  "HR",
  "Quality",
];

const PAYMENT_MODES = ["Cash", "Bank Transfer", "UPI", "Cheque", "Card", "Other"];

const defaultForm = () => ({
  category: "",
  expenseDate: new Date().toISOString().slice(0, 10),
  payee: "",
  department: "",
  mode: "Cash",
  billNo: "",
  referenceNo: "",
  description: "",
  notes: "",
  subtotal: "",
  taxAmount: "",
});

const currency = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const AccountsExpenses = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [query, setQuery] = useState("");

  const fetchRows = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get("/employee/accounts/expenses");
      setRows(normalizeRows(res.data));
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load expenses");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await API.get("/auth/me");
        const p = res.data?.employeeRole?.permissions || [];
        setPerms(Array.isArray(p) ? p : []);
      } catch {
        setPerms([]);
      }
    };
    fetchPerms();
  }, []);

  const canCreate = ["finance:expense:create"].some((p) => perms.includes(p));
  const canApprove = ["finance:expense:approve"].some((p) => perms.includes(p));

  const subtotalValue = Number(form.subtotal || 0);
  const taxValue = Number(form.taxAmount || 0);
  const totalValue = Number((subtotalValue + taxValue).toFixed(2));

  const openCreate = () => {
    setErr("");
    setForm(defaultForm());
    setShowCreate(true);
  };

  const buildPayload = () => {
    if (!String(form.category || "").trim()) return { error: "Category is required" };
    if (!String(form.description || "").trim()) return { error: "Description is required" };
    if (!Number.isFinite(subtotalValue) || subtotalValue <= 0) return { error: "Enter a valid base amount" };
    if (!Number.isFinite(taxValue) || taxValue < 0) return { error: "Enter a valid tax amount" };
    return {
      payload: {
        category: form.category,
        expenseDate: form.expenseDate || undefined,
        payee: form.payee || "",
        department: form.department || "",
        mode: form.mode || "",
        billNo: form.billNo || "",
        referenceNo: form.referenceNo || "",
        description: form.description || "",
        notes: form.notes || "",
        subtotal: subtotalValue,
        taxAmount: taxValue,
        amount: totalValue,
      },
    };
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    const { payload, error } = buildPayload();
    if (error) return setErr(error);
    try {
      setSubmitting(true);
      setErr("");
      await API.post("/employee/accounts/expenses", payload);
      setShowCreate(false);
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await API.patch(`/employee/accounts/expenses/${id}/status`, { status });
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Update failed");
    }
  };

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const text = [
        r.description,
        r.category,
        r.amount,
        r.payee,
        r.department,
        r.mode,
        r.billNo,
        r.referenceNo,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(q);
    });
  }, [rows, query]);

  const categoryTone = (value) => {
    const v = String(value || "").toLowerCase();
    if (v.includes("travel") || v.includes("fuel") || v.includes("freight")) return "travel";
    if (v.includes("meal") || v.includes("food")) return "meals";
    if (v.includes("maint") || v.includes("repair")) return "tech";
    if (v.includes("supply") || v.includes("pack")) return "supplies";
    return "default";
  };

  const statusTone = (value) => {
    const v = String(value || "").toLowerCase();
    if (v.includes("approved")) return "reimbursed";
    if (v.includes("pending")) return "pending";
    if (v.includes("draft")) return "draft";
    return "default";
  };

  return (
    <div className="expenses-page">
      <header className="expenses-header">
        <div>
          <h2 className="expenses-title">Expenses</h2>
          <p className="expenses-subtitle">Capture complete expense entries with billing, department, payment, and tax details.</p>
        </div>
        <div className="expenses-actions">
          <button className="expenses-btn ghost" type="button" onClick={fetchRows}>
            <span className="material-symbols-outlined">refresh</span>
            Refresh
          </button>
          {canCreate ? (
            <button className="expenses-btn primary" type="button" onClick={openCreate}>
              <span className="material-symbols-outlined">add</span>
              Create Expense
            </button>
          ) : null}
        </div>
      </header>

      <div className="expenses-toolbar">
        <label className="expenses-search">
          <span className="material-symbols-outlined">search</span>
          <input
            placeholder="Search by payee, category, bill no, reference..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <div className="expenses-filters">
          <button className="expenses-chip" type="button">
            {rows.length} Records
            <span className="material-symbols-outlined">receipt_long</span>
          </button>
          <button className="expenses-chip" type="button">
            Pending {rows.filter((row) => String(row.status || "").toLowerCase() === "pending").length}
            <span className="material-symbols-outlined">pending_actions</span>
          </button>
          <button className="expenses-chip" type="button">
            Total {currency(rows.reduce((sum, row) => sum + Number(row.amount || 0), 0))}
            <span className="material-symbols-outlined">payments</span>
          </button>
        </div>
      </div>

      {err && <Alert variant="danger" className="mx-4">{err}</Alert>}

      <div className="expenses-table-card">
        {loading ? (
          <div className="expenses-loading">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="expenses-table-wrap">
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>Expense Item</th>
                  <th>Category</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="text-right">Amount</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="expenses-empty">No expenses found</td>
                  </tr>
                ) : (
                  filteredRows.map((r, i) => (
                    <tr key={r._id || r.id || i}>
                      <td>
                        <div className="expense-item">
                          <span className="expense-title">{orDash(r.description || r.category)}</span>
                          <span className="expense-sub">
                            {[
                              r.payee || "No payee",
                              r.department || "No department",
                              r.billNo ? `Bill ${r.billNo}` : "",
                              r.referenceNo ? `Ref ${r.referenceNo}` : "",
                            ]
                              .filter(Boolean)
                              .join(" | ")}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`expense-pill cat ${categoryTone(r.category)}`}>
                          <span className="material-symbols-outlined">receipt_long</span>
                          {orDash(r.category)}
                        </span>
                      </td>
                      <td className="expense-date">{fmtDate(r.expenseDate || r.createdAt)}</td>
                      <td>
                        <span className={`expense-pill status ${statusTone(r.status)}`}>
                          <span className="dot" />
                          {orDash(r.status || "Pending")}
                        </span>
                      </td>
                      <td className="expense-amount">
                        {currency(r.amount)}
                        <div className="expense-amount-breakup">
                          Base {currency(r.subtotal || r.amount)} {Number(r.taxAmount || 0) > 0 ? `| Tax ${currency(r.taxAmount)}` : ""}
                        </div>
                      </td>
                      <td className="expense-actions">
                        {canApprove ? (
                          <>
                            <button className="expense-action approve" type="button" onClick={() => updateStatus(r._id, "approved")}>
                              Approve
                            </button>
                            <button className="expense-action reject" type="button" onClick={() => updateStatus(r._id, "rejected")}>
                              Reject
                            </button>
                          </>
                        ) : (
                          <button className="expense-action more" type="button">
                            <span className="material-symbols-outlined">more_horiz</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="expenses-pagination">
          <span>Showing 1-{Math.min(filteredRows.length, 10)} of {filteredRows.length} expenses</span>
          <div className="expenses-pages">
            <button type="button">
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <button type="button" className="active">1</button>
            <button type="button">2</button>
            <button type="button">3</button>
            <button type="button">
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      <Modal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        fullscreen
        scrollable
        dialogClassName="expenses-create-modal"
      >
        <form onSubmit={submitCreate}>
          <Modal.Header closeButton className="expenses-create-head">
            <div>
              <p className="expenses-create-kicker">Expense Workspace</p>
              <Modal.Title>Create Expense Entry</Modal.Title>
              <p className="expenses-create-sub">Enter complete accounting details so approval and reconciliation stay clean.</p>
            </div>
          </Modal.Header>
          <Modal.Body className="expenses-create-body">
            <section className="expenses-create-card">
              <h4>Basic Details</h4>
              <div className="expenses-create-grid">
                <label className="expenses-field">
                  <span>Category</span>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required>
                    <option value="">Select category</option>
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label className="expenses-field">
                  <span>Expense Date</span>
                  <input
                    type="date"
                    value={form.expenseDate}
                    onChange={(e) => setForm({ ...form, expenseDate: e.target.value })}
                    required
                  />
                </label>
                <label className="expenses-field">
                  <span>Payee / Vendor</span>
                  <input
                    value={form.payee}
                    onChange={(e) => setForm({ ...form, payee: e.target.value })}
                    placeholder="Who was paid?"
                  />
                </label>
                <label className="expenses-field">
                  <span>Department</span>
                  <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                    <option value="">Select department</option>
                    {DEPARTMENT_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label className="expenses-field">
                  <span>Payment Mode</span>
                  <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                    {PAYMENT_MODES.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label className="expenses-field">
                  <span>Reference No</span>
                  <input
                    value={form.referenceNo}
                    onChange={(e) => setForm({ ...form, referenceNo: e.target.value })}
                    placeholder="UTR / cheque / transaction ref"
                  />
                </label>
                <label className="expenses-field">
                  <span>Bill / Voucher No</span>
                  <input
                    value={form.billNo}
                    onChange={(e) => setForm({ ...form, billNo: e.target.value })}
                    placeholder="Bill no or voucher no"
                  />
                </label>
              </div>
            </section>

            <section className="expenses-create-card">
              <h4>Expense Description</h4>
              <label className="expenses-field">
                <span>Description</span>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What was this expense for?"
                  required
                />
              </label>
              <label className="expenses-field">
                <span>Internal Notes</span>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Approval note, project context, expense remarks..."
                />
              </label>
            </section>

            <section className="expenses-bottom-grid">
              <div className="expenses-create-card">
                <h4>Amount Details</h4>
                <div className="expenses-create-grid compact">
                  <label className="expenses-field">
                    <span>Base Amount</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.subtotal}
                      onChange={(e) => setForm({ ...form, subtotal: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </label>
                  <label className="expenses-field">
                    <span>Tax Amount</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.taxAmount}
                      onChange={(e) => setForm({ ...form, taxAmount: e.target.value })}
                      placeholder="0.00"
                    />
                  </label>
                </div>
              </div>
              <div className="expenses-create-card expenses-total-card">
                <h4>Summary</h4>
                <div className="expenses-total-row">
                  <span>Base Amount</span>
                  <strong>{currency(subtotalValue)}</strong>
                </div>
                <div className="expenses-total-row">
                  <span>Tax Amount</span>
                  <strong>{currency(taxValue)}</strong>
                </div>
                <div className="expenses-total-row grand">
                  <span>Total Expense</span>
                  <strong>{currency(totalValue)}</strong>
                </div>
                <p className="expenses-total-note">Total amount will be saved in accounts records with tax breakup.</p>
              </div>
            </section>
          </Modal.Body>
          <Modal.Footer className="expenses-create-foot">
            <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Saving..." : "Save Expense"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};
